// @flow
import { of, Observable } from 'rxjs';
import { map, catchError, mergeMap, takeUntil } from 'rxjs/operators';
import { ofType } from 'redux-observable';
import { getFlightApi, putFlightApi, endpoints } from './api';
import { setNewAlert } from '../alert/duck';

import type { HTTPError } from '../../error';
import type { Flight } from '../../entities';

export type EditFlightState = {
  +isPutting?: boolean;
  +putError?: HTTPError;
  +isFetching?: boolean;
  +fetchError?: HTTPError;
  +flight?: Flight;
  +successUpdate?: boolean;
};

type EditFlightAction = {
  +type: string;
  +flightDate?: string;
  +departureCountry?: string;
  +departureAdmin1?: number;
  +departureAdmin2?: number;
  +arrivalCountry?: string;
  +arrivalAdmin1?: number;
  +arrivalAdmin2?: number;
  +flight?: Flight;
  +id?: string;
  +error?: HTTPError;
};
// Actions
const EDIT_FLIGHT_GET_FLIGHT_REQUEST = 'EDIT_FLIGHT_GET_FLIGHT_REQUEST';
const EDIT_FLIGHT_GET_FLIGHT_SUCCESS = 'EDIT_FLIGHT_GET_FLIGHT_SUCCESS';
const EDIT_FLIGHT_GET_FLIGHT_ERROR = 'EDIT_FLIGHT_GET_FLIGHT_ERROR';

const EDIT_FLIGHT_PUT_FLIGHT_REQUEST = 'EDIT_FLIGHT_PUT_FLIGHT_REQUEST';
const EDIT_FLIGHT_PUT_FLIGHT_SUCCESS = 'EDIT_FLIGHT_PUT_FLIGHT_SUCCESS';
const EDIT_FLIGHT_PUT_FLIGHT_ERROR = 'EDIT_FLIGHT_PUT_FLIGHT_ERROR';

const EDIT_FLIGHT_RESET = 'EDIT_FLIGHT_RESET';

// Action Creators
export function getFlight(id: string): EditFlightAction {
  return {
    type: EDIT_FLIGHT_GET_FLIGHT_REQUEST,
    id,
  }
}

export function getFlightSuccess(flight: Flight): EditFlightAction {
  return {
    type: EDIT_FLIGHT_GET_FLIGHT_SUCCESS,
    flight,
  }
}

export function getFlightError(error: HTTPError): EditFlightAction {
  return {
    type: EDIT_FLIGHT_GET_FLIGHT_ERROR,
    error,
  }
}

export function editFlight(flightDate: string, departureCountry: string, departureAdmin1: number,
                           departureAdmin2: number, arrivalCountry: string, arrivalAdmin1: number,
                           arrivalAdmin2: number, id: string): EditFlightAction {
  return {
    type: EDIT_FLIGHT_PUT_FLIGHT_REQUEST,
    flightDate,
    departureCountry,
    departureAdmin1,
    departureAdmin2,
    arrivalCountry,
    arrivalAdmin1,
    arrivalAdmin2,
    id,
  };
}

export function editFlightSuccess(): EditFlightAction {
  return {
    type: EDIT_FLIGHT_PUT_FLIGHT_SUCCESS,
  };
}

export function editFlightError(error: HTTPError): EditFlightAction {
  return {
    type: EDIT_FLIGHT_PUT_FLIGHT_ERROR,
    error,
  }
}

export function resetState(): EditFlightAction {
  return {
    type: EDIT_FLIGHT_RESET,
  }
}

export const actions = {
  EDIT_FLIGHT_GET_FLIGHT_REQUEST,
  EDIT_FLIGHT_GET_FLIGHT_SUCCESS,
  EDIT_FLIGHT_GET_FLIGHT_ERROR,
  EDIT_FLIGHT_PUT_FLIGHT_REQUEST,
  EDIT_FLIGHT_PUT_FLIGHT_SUCCESS,
  EDIT_FLIGHT_PUT_FLIGHT_ERROR,
  EDIT_FLIGHT_RESET,
  getFlight,
  getFlightSuccess,
  getFlightError,
  editFlight,
  editFlightSuccess,
  editFlightError,
  resetState,
};

export const initialState: EditFlightState = {};

// Reducer
export default function reducer(state: EditFlightState = initialState,
                                action: EditFlightAction): EditFlightState {
  switch (action.type) {
    case EDIT_FLIGHT_GET_FLIGHT_REQUEST:
      return {
        isFetching: true,
      };
    case EDIT_FLIGHT_GET_FLIGHT_SUCCESS:
      return {
        flight: action.flight,
      };
    case EDIT_FLIGHT_GET_FLIGHT_ERROR:
      return {
        fetchError: action.error,
      };
    case EDIT_FLIGHT_PUT_FLIGHT_REQUEST:
      return {
        flight: state.flight,
        isPutting: true,
      };
    case EDIT_FLIGHT_PUT_FLIGHT_SUCCESS:
      return {
        successUpdate: true,
      };
    case EDIT_FLIGHT_PUT_FLIGHT_ERROR:
      return {
        flight: state.flight,
        putError: action.error,
      };
    case EDIT_FLIGHT_RESET:
      return initialState;
    default:
      return state;
  }
}

// Epics
export function fetchFlightEpic(action$: Observable<EditFlightAction>): Observable<EditFlightAction> {
  return action$.pipe(
    ofType(EDIT_FLIGHT_GET_FLIGHT_REQUEST),
    mergeMap(({ id }) =>
      getFlightApi(undefined, {
        route: endpoints.GET_FLIGHT.replace(':id', id),
      }).pipe(
        takeUntil(action$.pipe(ofType(EDIT_FLIGHT_RESET))),
        map((flight: Flight) => getFlightSuccess(flight)),
        catchError((e: HTTPError) => of(getFlightError(e))))
    ));
}

export function editFlightEpic(action$: Observable<EditFlightAction>): Observable<EditFlightAction> {
  return action$.pipe(
    ofType(EDIT_FLIGHT_PUT_FLIGHT_REQUEST),
    mergeMap(({ flightDate, id, }) => {
      return putFlightApi({ flightDate }, {
        route: endpoints.PUT_FLIGHT.replace(':id', id),
      }).pipe(
        mergeMap(() => of(
          editFlightSuccess(),
        )),
        catchError((e: HTTPError) => {
          if (Math.round(e.code / 100) === 5) {
            return of(editFlightError(e),
              setNewAlert('edit-flight', 'error', undefined, 'httpErrors.post.500'),
            );
          } else if (Math.round(e.code / 100) === 4) {
            return of(editFlightError(e),
              setNewAlert('edit-flight', 'error', undefined, 'httpErrors.post.400'),
            );
          }
          return of(editFlightError(e))
        }))
    }));
}

/*export function redirectAfterEditFlight(action$: ActionsObservable<EditFlightAction>) {
  return action$.pipe(
ofType(EDIT_FLIGHT_PUT_FLIGHT_SUCCESS)
    .mergeMap(({ id }) => Observable.of(push(`/flight/${id}/requests`)));
}*/



// WEBPACK FOOTER //
// ./src/modules/edit-flight/duck.js